class CartDrawerSection extends HTMLElement {
  cartUpdateUnsubscriber = undefined;
  constructor() {
    super();

    this.cartType = this.dataset.cartType;
    this.drawerClass = "wt-cart__drawer";
    this.drawer = this.querySelector(`.${this.drawerClass}`);
    this.classDrawerActive = `${this.drawerClass}--open`;
    this.pageOverlayClass = "page-overlay-cart";
    this.activeOverlayBodyClass = `${this.pageOverlayClass}-on`;
    this.body = document.body;
    this.triggerQuery = [
      `.wt-cart__trigger`,
      `.wt-cart__back-link`,
      `.${this.pageOverlayClass}`,
    ].join(", ");
    this.triggers = () => document.querySelectorAll(this.triggerQuery);
    this.isOpen = false;
    this.isCartPage = window.location.pathname === window.routes.cart_url;
    this.closeButton = () => this.querySelector(".wt-cart__drawer__close");
    this.mainTrigger = document.querySelector(".wt-cart__trigger");
    this.toggleEelements = () =>
      this.querySelectorAll(this.dataset.toggleTabindex);

    // Stores element that opened the drawer (for focus restore after close)
    this.openerEl = null;
    this.dispatchCountdownInterval = null;
  }

  connectedCallback() {
    if (this.cartType === "page" || this.isCartPage) {
      document.addEventListener("cart-drawer:refresh", (e) =>
        this.refreshCartDrawer(e),
      );
      return;
    }

    this.init();
    this.updateDispatchCountdown();
    this.dispatchCountdownInterval = window.setInterval(
      () => this.updateDispatchCountdown(),
      1000,
    );
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      if (this.isOpen) {
        setTabindex(this.toggleEelements(), "0");
        this.closeButton().focus();
      }
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
    if (this.dispatchCountdownInterval) {
      window.clearInterval(this.dispatchCountdownInterval);
    }
  }

  getTimeZoneOffset(date, timeZone) {
    const parts = new Intl.DateTimeFormat("en-GB", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    }).formatToParts(date);

    const values = Object.fromEntries(
      parts
        .filter((part) => part.type !== "literal")
        .map((part) => [part.type, Number(part.value)]),
    );

    return (
      Date.UTC(
        values.year,
        values.month - 1,
        values.day,
        values.hour,
        values.minute,
        values.second,
      ) - date.getTime()
    );
  }

  getUkLocalDate(year, month, day, hour, minute = 0, second = 0) {
    const utcGuess = new Date(Date.UTC(year, month - 1, day, hour, minute, second));
    const offset = this.getTimeZoneOffset(utcGuess, "Europe/London");
    return new Date(utcGuess.getTime() - offset);
  }

  getUkDateParts(date) {
    const parts = new Intl.DateTimeFormat("en-GB", {
      timeZone: "Europe/London",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(date);

    return Object.fromEntries(
      parts
        .filter((part) => part.type !== "literal")
        .map((part) => [part.type, Number(part.value)]),
    );
  }

  formatDispatchCountdown(milliseconds) {
    const totalSeconds = Math.max(0, Math.floor(milliseconds / 1000));
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0) {
      return `${hours}h ${String(minutes).padStart(2, "0")}m`;
    }

    return `${minutes}m ${String(seconds).padStart(2, "0")}s`;
  }

  updateDispatchCountdown() {
    const countdownValues = this.querySelectorAll("[data-cart-dispatch-countdown-value]");
    if (!countdownValues.length) return;

    const now = new Date();
    const ukToday = this.getUkDateParts(now);
    let cutoff = this.getUkLocalDate(ukToday.year, ukToday.month, ukToday.day, 15);

    if (now >= cutoff) {
      const nextUkDay = new Date(Date.UTC(ukToday.year, ukToday.month - 1, ukToday.day + 1));
      const nextUkParts = this.getUkDateParts(nextUkDay);
      cutoff = this.getUkLocalDate(nextUkParts.year, nextUkParts.month, nextUkParts.day, 15);
    }

    const countdown = this.formatDispatchCountdown(cutoff.getTime() - now.getTime());
    countdownValues.forEach((element) => {
      element.textContent = countdown;
    });
  }

  rememberOpener(e) {
    // Prefer currentTarget (the element with listener), fallback to activeElement
    const current = e?.currentTarget;
    const active = document.activeElement;

    // If click landed on svg/span inside button, climb up to interactive parent
    const interactive =
      current instanceof HTMLElement
        ? current.closest?.("button, a, [tabindex], input, select") || current
        : null;

    this.openerEl =
      interactive instanceof HTMLElement
        ? interactive
        : active instanceof HTMLElement
          ? active
          : null;
  }

  restoreFocusToOpener() {
    const el = this.openerEl;
    if (!el) return;
    if (!document.contains(el)) return;

    const isDisabled =
      el.hasAttribute("disabled") ||
      el.getAttribute("aria-disabled") === "true";
    if (isDisabled) return;

    // Wait one frame so DOM/classes settle after closing
    requestAnimationFrame(() => {
      el.focus?.();
    });
  }

  getFocusableElements() {
    const focusableElementsSelector =
      "button, [href], input, select, [tabindex]";
    const focusableElements = () =>
      Array.from(this.querySelectorAll(focusableElementsSelector)).filter(
        (el) => !el.hasAttribute("disabled") && el.tabIndex >= 0,
      );

    return {
      focusableElements,
      first: focusableElements()[0],
      last: focusableElements()[focusableElements().length - 1],
    };
  }

  temporaryHideFocusVisible() {
    document.body.classList.add("no-focus-visible");
  }

  onToggle() {
    if (this.hasAttribute("open")) {
      this.removeAttribute("open");
      this.isOpen = false;
      // this.mainTrigger.focus();
      this.temporaryHideFocusVisible();
      setTabindex(this.toggleEelements(), "-1");

      // Restore focus to the element that opened the drawer
      this.restoreFocusToOpener();
    } else {
      this.setAttribute("open", "");
      this.isOpen = true;
      this.closeButton().focus();
      this.temporaryHideFocusVisible();
      setTabindex(this.toggleEelements(), "0");
    }
  }

  toggleDrawerClasses() {
    this.onToggle();
    this.drawer.classList.toggle(this.classDrawerActive);
    this.body.classList.toggle(this.activeOverlayBodyClass);

    // dispatch a custom event on the document
    const eventName = this.isOpen
      ? PUB_SUB_EVENTS.cartDrawerOpen
      : PUB_SUB_EVENTS.cartDrawerClose;

    document.dispatchEvent(
      new CustomEvent(eventName, {
        bubbles: true,
      }),
    );
  }

  init() {
    this.addEventListener("keydown", (e) => {
      const isTabPressed =
        e.key === "Tab" || e.keyCode === 9 || e.code === "Tab";
      const { first, last } = this.getFocusableElements();

      if (e.key === "Escape" || e.keyCode === 27 || e.code === "Escape") {
        if (this.isOpen) {
          this.toggleDrawerClasses();
        }
      }

      if (isTabPressed) {
        if (e.shiftKey && document.activeElement === first) {
          last.focus();
          e.preventDefault();
        } else if (!e.shiftKey && document.activeElement === last) {
          first.focus();
          e.preventDefault();
        }
      }
    });

    this.triggers().forEach((trigger) => {
      trigger.addEventListener("click", (e) => {
        e.preventDefault();

        // Save opener only when opening
        if (!this.isOpen) this.rememberOpener(e);

        this.toggleDrawerClasses();
      });
    });

    this.addEventListener("click", (e) => {
      if (e.target.classList.contains("wt-cart__drawer__close")) {
        e.preventDefault();
        this.toggleDrawerClasses();
      }

      const crossSellButton = e.target.closest?.(".js-cart-cross-sell-add");
      if (crossSellButton) {
        e.preventDefault();
        this.addCrossSellToCart(crossSellButton);
        return;
      }

      const subscriptionButton = e.target.closest?.(".js-cart-subscription-submit");
      if (subscriptionButton) {
        e.preventDefault();
        this.updateSubscriptionOffer(subscriptionButton);
        return;
      }

      const subscriptionUpgradeButton = e.target.closest?.(".js-cart-subscription-upgrade");
      if (subscriptionUpgradeButton) {
        e.preventDefault();
        this.updateSubscriptionUpgrade(subscriptionUpgradeButton);
      }
    });

    document.addEventListener("cart-drawer:refresh", (e) =>
      this.refreshCartDrawer(e),
    );

    this.addEventListener("change", (event) => {
      const subscriptionSelect = event.target.closest(".js-cart-subscription-select");
      if (subscriptionSelect) {
        this.updateSubscriptionOfferDetails(subscriptionSelect);
        return;
      }

      const planSelect = event.target.closest(".js-cart-plan-select");
      if (!planSelect) return;
      this.updateSubscriptionPlan(planSelect);
    });
  }

  updateSubscriptionOfferDetails(planSelect) {
    const selectedOption = planSelect.selectedOptions?.[0];
    const subscriptionCard = planSelect.closest("[data-cart-subscription-upsell]");
    if (!selectedOption || !subscriptionCard) return;

    const price = subscriptionCard.querySelector("[data-cart-subscription-price]");
    const compare = subscriptionCard.querySelector("[data-cart-subscription-compare]");
    const save = subscriptionCard.querySelector("[data-cart-subscription-save]");
    const cost = subscriptionCard.querySelector("[data-cart-subscription-cost]");

    if (price) price.textContent = selectedOption.dataset.price || "";

    if (compare) {
      compare.textContent = selectedOption.dataset.comparePrice || "";
      compare.hidden = !selectedOption.dataset.comparePrice;
    }

    if (save) {
      save.textContent = selectedOption.dataset.saveLabel || "";
      save.hidden = !selectedOption.dataset.saveLabel;
    }

    if (cost) {
      cost.textContent = selectedOption.dataset.costPerDose || "Lower";
    }
  }

  async postCartRequest(url, payload) {
    const response = await fetch(url, {
      ...fetchConfig(),
      ...{ body: JSON.stringify(payload) },
    });
    const parsedResponse = await response.json();

    if (!response.ok || parsedResponse.errors) {
      const message =
        parsedResponse.description ||
        parsedResponse.message ||
        parsedResponse.errors ||
        window.cartStrings?.error;
      throw new Error(message);
    }

    return parsedResponse;
  }

  async getCartLineProperties(lineIndex) {
    const response = await fetch(`${routes.cart_url}.js`, {
      headers: { Accept: "application/json" },
    });
    if (!response.ok) return null;

    const cart = await response.json();
    return cart.items?.[lineIndex - 1]?.properties || null;
  }

  generateSubscriptionLineId() {
    return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  }

  withSubscriptionLineProperty(properties = {}) {
    const nextProperties = { ...properties };
    if (!nextProperties._subscription_line_id) {
      nextProperties._subscription_line_id = this.generateSubscriptionLineId();
    }
    return nextProperties;
  }

  showCartError(message) {
    const errors =
      this.querySelector("#CartDrawer-CartErrors") ||
      this.querySelector("#cart-errors");
    if (errors) errors.textContent = message || window.cartStrings?.error;
  }

  refreshAndPublish(source) {
    document.dispatchEvent(new CustomEvent("cart-drawer:refresh", { bubbles: true }));
    publish(PUB_SUB_EVENTS.cartUpdate, { source });
  }

  async addCrossSellToCart(button) {
    const variantId = Number(button.dataset.variantId || 0);
    if (!variantId) return;

    button.disabled = true;
    button.classList.add("loading");

    try {
      // MARCA Media - 2026-05-04: add cross-sells directly from the drawer card without opening quick-buy.
      await this.postCartRequest(routes.cart_add_url, {
        id: variantId,
        quantity: 1,
      });
      this.refreshAndPublish("cart-drawer-cross-sell");
    } catch (error) {
      console.error(error);
      this.showCartError(error.message);
    } finally {
      button.disabled = false;
      button.classList.remove("loading");
    }
  }

  async updateSubscriptionOffer(button) {
    const subscriptionCard = button.closest("[data-cart-subscription-upsell]");
    const planSelect = subscriptionCard?.querySelector(".js-cart-subscription-select");
    const nextSelection = String(planSelect?.value || "");
    if (!subscriptionCard || !nextSelection) return;

    const [variantId, sellingPlanId] = nextSelection.split("|");
    const lineIndex = Number(button.dataset.lineIndex || 0);
    const lineQuantity = Number(button.dataset.lineQuantity || 1);
    const currentVariantId = String(button.dataset.currentVariantId || "");
    const currentSellingPlanId = String(button.dataset.currentSellingPlanId || "");
    // MARCA Media - 2026-05-21: converting one-time cart lines to subscription should always create a single subscription item.
    const nextLineQuantity = currentSellingPlanId ? lineQuantity : 1;

    if (!variantId || !sellingPlanId || !lineIndex || lineQuantity < 1) return;
    if (variantId === currentVariantId && sellingPlanId === currentSellingPlanId) return;

    button.disabled = true;
    button.classList.add("loading");

    try {
      await this.applySubscriptionSelection({
        variantId,
        sellingPlanId,
        lineIndex,
        lineQuantity: nextLineQuantity,
        currentVariantId,
        source: "cart-drawer-subscription-upsell",
      });
    } catch (error) {
      console.error(error);
      this.showCartError(error.message);
    } finally {
      button.disabled = false;
      button.classList.remove("loading");
    }
  }

  async updateSubscriptionUpgrade(button) {
    const variantId = String(button.dataset.variantId || "");
    const sellingPlanId = String(button.dataset.sellingPlanId || "");
    const lineIndex = Number(button.dataset.lineIndex || 0);
    const lineQuantity = Number(button.dataset.lineQuantity || 1);
    const currentVariantId = String(button.dataset.currentVariantId || "");
    const currentSellingPlanId = String(button.dataset.currentSellingPlanId || "");

    if (!variantId || !sellingPlanId || !lineIndex || lineQuantity < 1) return;
    if (variantId === currentVariantId && sellingPlanId === currentSellingPlanId) return;

    button.disabled = true;
    button.classList.add("loading");

    try {
      // MARCA Media - 2026-05-06: one-click length upgrades use the shared subscription plan mutation path.
      await this.applySubscriptionSelection({
        variantId,
        sellingPlanId,
        lineIndex,
        lineQuantity,
        currentVariantId,
        source: "cart-drawer-subscription-upgrade",
      });
    } catch (error) {
      console.error(error);
      this.showCartError(error.message);
    } finally {
      button.disabled = false;
      button.classList.remove("loading");
    }
  }

  async applySubscriptionSelection({
    variantId,
    sellingPlanId,
    lineIndex,
    lineQuantity,
    currentVariantId,
    source,
  }) {
    const sellingPlan = Number(sellingPlanId);
    const properties = (await this.getCartLineProperties(lineIndex)) || {};
    const needsSubscriptionLineId =
      sellingPlan > 0 && !properties._subscription_line_id;

    // MARCA Media - 2026-07-01: cart/change cannot add line properties, so re-add when a unique subscription line id is required.
    if (variantId === currentVariantId && needsSubscriptionLineId) {
      await this.postCartRequest(routes.cart_change_url, {
        line: lineIndex,
        quantity: 0,
      });

      await this.postCartRequest(routes.cart_add_url, {
        id: Number(variantId),
        quantity: lineQuantity,
        selling_plan: sellingPlan,
        properties: this.withSubscriptionLineProperty(properties),
      });
    } else if (variantId === currentVariantId) {
      await this.postCartRequest(routes.cart_change_url, {
        line: lineIndex,
        quantity: lineQuantity,
        selling_plan: sellingPlan,
      });
    } else {
      await this.postCartRequest(routes.cart_change_url, {
        line: lineIndex,
        quantity: 0,
      });

      const addPayload = {
        id: Number(variantId),
        quantity: lineQuantity,
        selling_plan: sellingPlan,
        properties: this.withSubscriptionLineProperty(properties),
      };

      await this.postCartRequest(routes.cart_add_url, addPayload);
    }

    this.refreshAndPublish(source);
  }

  async updateSubscriptionPlan(planSelect) {
    const nextSelection = String(planSelect.value || "");
    const currentSelection = String(planSelect.dataset.currentSelection || "");
    if (!nextSelection || nextSelection === currentSelection) return;

    const [variantId, sellingPlanId] = nextSelection.split("|");
    const [currentVariantId] = currentSelection.split("|");
    const lineIndex = Number(planSelect.dataset.lineIndex || 0);
    const lineQuantity = Number(planSelect.dataset.lineQuantity || 1);

    if (!variantId || !sellingPlanId || !lineIndex || lineQuantity < 1) return;

    planSelect.disabled = true;

    try {
      await this.applySubscriptionSelection({
        variantId,
        sellingPlanId,
        lineIndex,
        lineQuantity,
        currentVariantId,
        source: "cart-drawer-plan-select",
      });
    } catch (error) {
      console.error(error);
      this.showCartError(error.message);
    } finally {
      planSelect.disabled = false;
    }
  }

  renderContents(parsedState, isClosedCart = true) {
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector,
      );
    });
    this.updateDispatchCountdown();

    if (isClosedCart) {
      setTimeout(() => {
        this.toggleDrawerClasses();
        if (this.isOpen) {
          this.closeButton().focus();
        }
      });
    }
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: "cart-drawer",
        selector: "#CartDrawer",
      },
      {
        id: "cart-icon-bubble",
      },
    ];
  }

  refreshCartDrawer(e) {
    const sectionsToRender = this.getSectionsToRender();
    fetch(
      `${window.Shopify.routes.root}?sections=${sectionsToRender[0].id},${sectionsToRender[1].id}`,
    )
      .then((response) => response.json())
      .then((response) => {
        const parsedState = {
          sections: response,
        };
        this.renderContents(parsedState, false);
      })
      .catch((e) => {
        console.log(e);
      });
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define("cart-drawer", CartDrawerSection);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: ".drawer__inner",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
    ];
  }
}

customElements.define("cart-drawer-items", CartDrawerItems);
